/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { UnsubscribeFn } from '@oneaudi/dealer-context-service';
import type { EnumerableFootnote } from '@oneaudi/footnote-reference-service';
import { NotificationTypeV1 } from '@oneaudi/audi-notification-display-service';
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import type { IHeaderAction } from '../../actions/header-actions';
import { createSetActiveItemAction } from '../../actions/header-actions';
import { DealerContext, OidcUserContext } from '../../context';
import { getDealerData } from '../../graphql/get-dealer';
import type {
  AudiHeaderDataManagerProps,
  Dealer,
} from '../../interfaces/header-components.interfaces';
import type HeaderResponse from '../../interfaces/header-response.interfaces';
import type { HeaderState } from '../../reducers/header-reducer';
import { headerReducer } from '../../reducers/header-reducer';
import { createUseFootnoteReferenceServiceTextParserHook } from '../../services/hooks';
import { fetchHeaderConfig } from '../../services/navigation-service';
import { cleanNavigationEntries } from '../../utils/clean-navigation-entries';
import {
  AUTH_OIDC_USER_INFO,
  DEALER_CONNECTED_NOTIFICATION,
  DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE,
  DEALER_CONTEXT_UNSUBSCRIBE,
  KEY_DEALER_DATA,
} from '../../utils/globals';
import { isEmpty } from '../../utils/is-empty';
import type { NormalizeContentConfig } from '../../utils/normalize-falcon-data';
import { normalizeContentHeadless } from '../../utils/normalize-falcon-data';
import { replacePartnerId, renderPartnerId } from '../../utils/replace-partner';
import AudiHeader from './AudiHeader';
import { ServiceContext } from '../../context/ServiceContext';
import { ConfigContext } from '../../context/ConfigContext';
import { matchCountryUrlOrigin } from '../../utils/match-url-pattern';
import { useAuthFetch } from '../../hooks/use-auth-fetch';
import type { OidcUser } from '../../types/oidc-user-info';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';
import { Icon } from '@oneaudi/unified-web-components';
import { removeSessionData } from '../../utils/session-utils';
import { getDealerSearchHashParam } from '../../utils/append-partner-id';
import { isDealerDomain } from '../../utils/is-dealer-domain';

const AudiHeaderDataManager: React.FC<AudiHeaderDataManagerProps> = ({
  ssrHeaderData,
  headerConfigDataUrl,
  enablePartnerIdReplacement,
  contentHeadless,
}) => {
  const {
    dealerContextService,
    footnoteReferenceService,
    headerStateService,
    notificationDisplayService,
  } = useContext(ServiceContext);
  const {
    renderDealerContext,
    useOneLayer,
    useUserMenu,
    disableMyAudiMenu,
    tierOneUrl,
    OneShopEnabled,
    dealerSearch,
    dealerSearchGeolocationEnabled,
  } = useContext(ConfigContext);
  const isOneCms = contentHeadless?.__type === 'aem-headless';
  const [footnotes, setFootnotes] = useState([]);
  const [partnerId, setPartnerId] = useState(undefined);
  const [partnerIdQueryString, setPartnerIdQueryString] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [headerConfig, setHeaderConfig] = useState<HeaderResponse | undefined>(ssrHeaderData);
  const [displayUserMenu, setDisplayUserMenu] = useState<boolean>(useUserMenu || false);
  const [dealerData, setDealerData] = useState<Dealer | undefined>(undefined);
  const dealerConnectedNofication = useI18n(messages.dealership_connected_notification);
  const isTierOneDomain = isDealerDomain(tierOneUrl);

  const wrapperElement = useRef<HTMLDivElement>(null);
  const navigationWrapperElement = useRef<HTMLDivElement>(null);
  const actionWrapperElement = useRef<HTMLDivElement>(null);
  const logoWrapperElement = useRef<HTMLDivElement>(null);
  const dealerId = dealerContextService?.getSelectedDealer();
  // get OIDC user info if user is authenticated
  const {
    data: oidcUser,
    loading,
    error,
  }: {
    data: OidcUser | null;
    loading: boolean;
    error: string | null;
  } = useAuthFetch(
    AUTH_OIDC_USER_INFO.url,
    {
      method: 'GET',
      resourceHostId: AUTH_OIDC_USER_INFO.resourceHostId,
    },
    AUTH_OIDC_USER_INFO.sessionKey,
  );

  const useFootnoteReferenceServiceTextParserHook = createUseFootnoteReferenceServiceTextParserHook(
    {
      footnotes,
    },
  );

  const [headerState, headerDispatch] = useReducer<React.Reducer<HeaderState, IHeaderAction>>(
    headerReducer,
    {
      activeItem: {
        anchor: null,
        index: -1,
        navWrapperWidth: 0,
        showSearch: false,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
      },
    },
  );
  // logic to disable user menu on tier 3 markets
  useEffect(() => {
    if (disableMyAudiMenu) {
      if (useUserMenu && !matchCountryUrlOrigin(window.location.origin, tierOneUrl || '')) {
        setDisplayUserMenu(false);
      }
    }
  }, [disableMyAudiMenu, tierOneUrl, useUserMenu]);

  useEffect(() => {
    let unsubscribeDealerContext: UnsubscribeFn;

    if (renderDealerContext) {
      unsubscribeDealerContext = dealerContextService.subscribe((dealerContext) => {
        if (typeof window !== 'undefined' && dealerContext) {
          // if dealerSearchGeolocationEnabled is enabled, First check if KEY_DEALER_DATA is set to unsubscribe
          if (dealerSearchGeolocationEnabled) {
            const dealerStore = sessionStorage.getItem(KEY_DEALER_DATA);
            // if true the user has volunteered to be unsubscribed from autoconnected dealer
            if (dealerStore === DEALER_CONTEXT_UNSUBSCRIBE) {
              removeSessionData(KEY_DEALER_DATA); // remove storage KEY_DEALER_DATA
            }
          }

          // if true the user has volunteered to be unsubscribed from autoconnected dealer
          const autoConnectUnsubscribe = Boolean(
            sessionStorage.getItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE),
          );

          const dealerIdFromContext = `${dealerContext?.market}${dealerContext?.brand}${dealerContext?.id}`;
          const thePartnerId = renderPartnerId(dealerContext?.id, dealerContext?.market, isOneCms);
          setPartnerIdQueryString(thePartnerId);
          // re-check KEY_DEALER_DATA storage
          const store = sessionStorage.getItem(KEY_DEALER_DATA);

          // in the event a user decides to reconnect to a dealership via "select a dealer", check for params created by the dealer search FA
          let hasConsumerLocationsQueryString: boolean = false;
          if (autoConnectUnsubscribe && store && store !== DEALER_CONTEXT_UNSUBSCRIBE) {
            hasConsumerLocationsQueryString = getDealerSearchHashParam('consumerLocations'); // 'consumerLocations' the param created by the dealer search FA
          }

          if (store !== DEALER_CONTEXT_UNSUBSCRIBE) {
            const storageData: Dealer = JSON.parse(store || '{}');
            if (
              storageData?.dealerId?.toString() !== dealerIdFromContext &&
              !hasConsumerLocationsQueryString
            ) {
              getDealerData(process.env.GRAPHQL_DEALER_ENDPOINT, dealerIdFromContext)
                .then((data) => {
                  if (data) {
                    const { dealerById } = data;
                    setDealerData(dealerById);
                    sessionStorage.setItem(KEY_DEALER_DATA, JSON.stringify(dealerById));
                  }
                })
                .catch(() => {
                  if (!isEmpty(storageData)) {
                    setDealerData(storageData);
                  }

                  console.error(`[fa-nemo-header] dealer id ${dealerIdFromContext} not found`);
                });
            } else if (!isEmpty(storageData)) {
              setDealerData(storageData);
            }
          }
        } else setPartnerIdQueryString('');
      });
    }
    return (): void => {
      if (renderDealerContext) {
        unsubscribeDealerContext();
      }
    };
  }, [dealerContextService, renderDealerContext]);

  useEffect(() => {
    function createNotification() {
      const isOpen: boolean = notificationDisplayService.isOpen('dealer-search-notfication');
      const notificationConnected = sessionStorage.getItem(DEALER_CONNECTED_NOTIFICATION);
      // only create notification once when connected to dealer
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      if (!isOpen && Boolean(notificationConnected) === false) {
        notificationDisplayService.createNotification(
          NotificationTypeV1.POPUP_GENERIC,
          'dealer-search-notfication',
          dealerConnectedNofication,
          {
            title: `${dealerData?.additionalData?.displayName || dealerData?.name}`,
            userCloseable: false,
            icon: <Icon name="poi-active" size="s" />,
            autoCloseAfter: 5000,
          },
        );
        sessionStorage?.setItem(DEALER_CONNECTED_NOTIFICATION, 'true');
      }
    }

    // render notification only once when dealer has been coonected via the dealer search FA
    if (dealerData && renderDealerContext && isTierOneDomain) {
      createNotification();
    }
  }, [renderDealerContext, dealerData, isTierOneDomain]);

  // if no dealer context session saved anymore or removed from another FA, set KEY_DEALER_DATA to DEALER_CONTEXT_UNSUBSCRIBE
  useEffect(() => {
    if (!dealerId) {
      const storageData: Dealer | string | null = sessionStorage.getItem(KEY_DEALER_DATA);
      if (storageData && storageData !== DEALER_CONTEXT_UNSUBSCRIBE) {
        sessionStorage.removeItem(KEY_DEALER_DATA); // remove storage KEY_DEALER_DATA
        dealerContextService.setSelectedDealer(undefined);
        setDealerData(undefined);
      }
    }
  }, [dealerId]);

  useEffect(() => {
    if (window && window.location.href.indexOf('debug=true') !== -1) {
      headerStateService.setDebugMode(true);
    }
    if (footnoteReferenceService) {
      footnoteReferenceService.registerCallback((_footnotes: EnumerableFootnote[]) => {
        // @ts-ignore
        setFootnotes(_footnotes);
      });
    }

    return (): void => {
      if (window) {
        if (window.location.href.indexOf('debug=true') !== -1) {
          headerStateService.setDebugMode(true);
        } else {
          headerStateService.setDebugMode(false);
        }
      }
      if (footnoteReferenceService) {
        footnoteReferenceService.removeFootnoteReferences();
      }
    };
  }, []);

  useEffect(() => {
    if (navigationWrapperElement?.current) {
      setIsLoading(false);
    }
  }, [navigationWrapperElement]);

  useEffect(() => {
    if (!isOneCms) {
      // always load clientside again, to be sure, not to work with a cached version from SSR
      fetchHeaderConfig(headerConfigDataUrl)
        .then((response) => {
          setHeaderConfig(
            cleanNavigationEntries(
              enablePartnerIdReplacement ? replacePartnerId(response, partnerId) : response,
            ),
          );
        })

        .catch((headerConfigError) => {
          console.error(headerConfigError);
        });
    } else {
      const normalizeConfig: NormalizeContentConfig = { tierOneUrl, OneShopEnabled };
      setHeaderConfig(
        enablePartnerIdReplacement
          ? replacePartnerId(
              normalizeContentHeadless(contentHeadless, normalizeConfig, partnerIdQueryString),
              partnerId,
            )
          : normalizeContentHeadless(contentHeadless, normalizeConfig, partnerIdQueryString),
      );
    }
    setIsLoading(false);
  }, [headerConfigDataUrl, ssrHeaderData, partnerId, partnerIdQueryString]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !enablePartnerIdReplacement ||
      !(window.microkernel && window.microkernel.stateRegistry)
    ) {
      return (): void => {
        // Do nothing
      };
    }

    const partnerCallback = (state: React.SetStateAction<undefined>): void => {
      setPartnerId(state);
    };

    window.microkernel.stateRegistry.subscribeToStore('dbadDealerStore', partnerCallback);

    return (): void => {
      window.microkernel.stateRegistry.unsubscribeFromStore('dbadDealerStore', partnerCallback);
    };
  }, [enablePartnerIdReplacement, setPartnerId]);

  const removeDealer = useCallback((): void => {
    setDealerData(undefined);
    dealerContextService.setSelectedDealer(undefined); // remove dealer-context from session storage
    // only set KEY_DEALER_DATA to DEALER_CONTEXT_UNSUBSCRIBE if auto connect dealer search geolocation is enabled
    if (dealerSearchGeolocationEnabled) {
      const url = window.location.href;
      const urlObj = new URL(url);
      // Remove partner_id parameter
      urlObj.searchParams.delete('partner_id');
      // update url
      window.history.replaceState({}, document.title, urlObj.toString());
      sessionStorage?.setItem(KEY_DEALER_DATA, DEALER_CONTEXT_UNSUBSCRIBE);
      sessionStorage?.setItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE, 'true');
    }

    // No dealerSearch config enabled keep original DEALER_CONTEXT_UNSUBSCRIBE functionality
    if (!dealerSearch) {
      sessionStorage?.setItem(KEY_DEALER_DATA, DEALER_CONTEXT_UNSUBSCRIBE);
    }

    // remove notification connected session
    sessionStorage?.removeItem(DEALER_CONNECTED_NOTIFICATION);
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
      }),
    );
  }, [setDealerData]);

  return (
    <OidcUserContext.Provider value={{ oidcUser, loading, error }}>
      <DealerContext.Provider value={{ renderDealerContext, dealerData, removeDealer }}>
        <AudiHeader
          wrapperElement={wrapperElement}
          navigationWrapperElement={navigationWrapperElement}
          actionWrapperElement={actionWrapperElement}
          logoWrapperElement={logoWrapperElement}
          headerConfig={headerConfig}
          headerDispatch={headerDispatch}
          headerState={headerState}
          isLoading={isLoading}
          useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
          useOneLayer={useOneLayer}
          useUserMenu={displayUserMenu}
          isOneCms={isOneCms}
          data-tracking-exclude
        />
      </DealerContext.Provider>
    </OidcUserContext.Provider>
  );
};

export default AudiHeaderDataManager;
